'use client';

import { useEffect, useState } from 'react';
import ShowLadderItem from '@/components/ShowLadderItem';
import type { Event_Entry } from '@/types/graphql';
import { breakpoints } from '@/utils/breakpoints';
import { getVenueMonth } from '@/utils/dateFormat';

interface EventsLadderProps {
    events: Event_Entry[];
}

export default function EventsLadder({ events }: EventsLadderProps) {
    const [columns, setColumns] = useState(1);

    // Create an array of empty arrays, one for each column
    const columnItems: Event_Entry[][] = Array.from({ length: columns }, () => []);

    useEffect(() => {
        const updateColumns = () => {
            if (window.innerWidth >= breakpoints.lg) {
                setColumns(3);
            } else if (window.innerWidth >= breakpoints.sm) {
                setColumns(2);
            } else {
                setColumns(1);
            }
        };

        // Set initial column count
        updateColumns();

        // Update column count on window resize
        window.addEventListener('resize', updateColumns);

        // Cleanup event listener on component unmount
        return () => window.removeEventListener('resize', updateColumns);
    }, []);

    // Calculate the number of items per column
    const itemsPerColumn = Math.ceil(events.length / columns);

    // Distribute items into columns
    for (let i = 0; i < columns; i++) {
        columnItems[i] = events.slice(i * itemsPerColumn, (i + 1) * itemsPerColumn);
    }

    const getColorByMonth = (dateString: string) => {
        const month = getVenueMonth(dateString); // 0 = January, 11 = December
        const colors = [
            'bg-haug-lila-100 text-white', // First color
            'bg-haug-coral-100 text-white', // Second color
            'bg-haug-blue-100 text-white', // Third color
        ];
        return colors[month % colors.length]; // Cycle through colors
    };

    return (
        <div id="show-ladder" className="w-full">
            <div className="grid w-full grid-cols-1 gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
                {columnItems.map((column, i) => {
                    // Create a stable key based on the first event's ID in the column
                    const columnKey = column.length > 0 ? `column-${column[0].id}` : `column-${i}`;
                    return (
                        <div className="grid grid-cols-1 gap-y-4" key={columnKey}>
                            {column.map((event) => {
                                if (!event.eventProgramStart) {
                                    return null;
                                }

                                const colorClass = getColorByMonth(event.eventProgramStart);

                                return (
                                    <ShowLadderItem
                                        key={event.id}
                                        event={event}
                                        className={colorClass}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
